import { User, Mail, Calendar, Shield, CreditCard } from 'lucide-react';
import { useAuth } from '../stores/supabaseAuthStore';
import { useUserDisplay } from '../hooks/useUserDisplay';
import { SubscriptionStatus } from './SubscriptionStatus';

function ProfilePage() {
  const { isAuthenticated } = useAuth();
  const {
    user,
    getUserDisplayName,
    getUserInitials,
    getAuthProvider,
    getUserEmail,
    isEmailVerified,
    getMemberSinceDate,
  } = useUserDisplay();
  // Subscription data is handled by SubscriptionGate

  // Show loading or authentication required state
  if (!isAuthenticated || !user) {
    return (
      <div className="flex-1 bg-kind-deep p-4 lg:p-8 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-xl font-title font-medium text-kind-text-primary mb-2">
            Authentication Required
          </h2>
          <p className="font-body font-light text-kind-text-secondary">
            Please sign in to view your profile.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 bg-kind-deep p-4 lg:p-8">
      <div className="max-w-2xl mx-auto">
        {/* Mobile Header */}
        <div className="lg:hidden mb-6">
          <h1 className="text-2xl font-title font-medium text-kind-text-primary">Profile</h1>
        </div>

        {/* Desktop Header */}
        <div className="hidden lg:block mb-8">
          <h1 className="text-4xl font-title font-medium text-kind-text-primary mb-2">Profile</h1>
          <p className="font-body font-light text-kind-text-secondary">
            Manage your account information
          </p>
        </div>

        {/* Profile Card */}
        <div className="bg-kind-graphite rounded-2xl p-4 lg:p-8 border border-gray-300 dark:border-white/10">
          {/* Avatar Section */}
          <div className="flex flex-col sm:flex-row items-center sm:items-start space-y-4 sm:space-y-0 sm:space-x-6 mb-6 lg:mb-8">
            <div className="w-20 h-20 lg:w-24 lg:h-24 bg-gradient-to-br from-kind-teal to-kind-blue rounded-full flex items-center justify-center">
              <span className="text-2xl lg:text-3xl font-bold text-white">{getUserInitials()}</span>
            </div>
            <div className="text-center sm:text-left">
              <h2 className="text-xl lg:text-2xl font-title font-medium text-kind-text-primary mb-1">
                {getUserDisplayName()}
              </h2>
              <div className="flex items-center justify-center sm:justify-start space-x-2">
                <Shield className="w-4 h-4 text-kind-purple" />
                <span className="text-kind-purple font-medium">{getAuthProvider()} Account</span>
              </div>
            </div>
          </div>

          {/* Profile Information */}
          <div className="space-y-6">
            {/* Name Field */}
            <div>
              <label className="block text-sm font-medium text-kind-text-secondary mb-2">
                Full Name
              </label>
              <div className="flex items-center space-x-3 bg-kind-container rounded-xl px-4 py-3">
                <User className="w-5 h-5 text-kind-text-secondary" />
                <span className="text-kind-text-primary">{getUserDisplayName()}</span>
              </div>
            </div>

            {/* Email Field */}
            <div>
              <label className="block text-sm font-medium text-kind-text-secondary mb-2">
                Email Address
              </label>
              <div className="flex items-center space-x-3 bg-kind-container rounded-xl px-4 py-3">
                <Mail className="w-5 h-5 text-kind-text-secondary" />
                <span className="text-kind-text-primary">{getUserEmail()}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Additional Info */}
        <div className="mt-6 lg:mt-8 space-y-6">
          {/* Account Information */}
          <div className="bg-kind-graphite rounded-2xl p-4 lg:p-6 border border-gray-300 dark:border-white/10">
            <h3 className="text-lg lg:text-xl font-title font-medium text-kind-text-primary mb-4">
              Account Information
            </h3>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <Calendar className="w-5 h-5 text-kind-text-secondary" />
                  <span className="text-kind-text-secondary">Member Since</span>
                </div>
                <span className="text-kind-text-primary">{getMemberSinceDate()}</span>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <Shield className="w-5 h-5 text-kind-text-secondary" />
                  <span className="text-kind-text-secondary">Auth Provider</span>
                </div>
                <span className="text-kind-text-primary">{getAuthProvider()}</span>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <Mail className="w-5 h-5 text-kind-text-secondary" />
                  <span className="text-kind-text-secondary">Email Verified</span>
                </div>
                <span
                  className={`px-2 py-1 rounded-full text-xs font-medium ${
                    isEmailVerified()
                      ? 'bg-green-900/20 text-green-400'
                      : 'bg-yellow-900/20 text-yellow-400'
                  }`}
                >
                  {isEmailVerified() ? 'Verified' : 'Pending'}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <CreditCard className="w-5 h-5 text-kind-text-secondary" />
                  <span className="text-kind-text-secondary">Subscription</span>
                </div>
                <SubscriptionStatus showUpgradeButton={false} />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ProfilePage;
